/* eslint-disable react/no-unescaped-entities */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

const INDUSTRY_OPTIONS = [
  "", "Contractor / Home services", "Construction", "Real estate",
  "Property management", "Law firm", "Accounting", "Insurance", "Recruiting",
  "Dental / Medical practice", "Restaurant / Café", "Retail", "Salon / Barber",
  "Fitness studio", "Daycare / Childcare", "Nonprofit", "Hospitality",
  "Coworking / Real estate", "Professional services", "Other",
];

const EMPLOYEE_RANGES = [
  "", "1 (owner only)", "2–5", "6–10", "11–25", "26–50", "51–100", "100+",
];

export default function BookEfficiencyReview() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", business_name: "", work_email: "",
    phone: "", website: "", industry: "", employees: "",
    primary_challenge: "", time_consuming_task: "", lead_channels: "",
    software_used: "", preferred_time: "", consent: true,
  });
  const [step, setStep] = useState(1);            // 1=contact, 2=business, 3=done
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({
    ...f,
    [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
  }));

  const step1Valid = form.first_name && form.last_name && form.business_name && form.work_email;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setError("Please confirm consent to be contacted.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/operations/leads", form);
      setSubmitted(true);
      setStep(3);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Submission failed. Please try again.";
      setError(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-efficiency-review-page" className="min-h-screen bg-[#080807] text-[#f4efe6]">
      <Helmet>
        <title>Book a Free AI Efficiency Review — The Entity Agency</title>
        <meta name="description" content="A focused 15–20 minute conversation to identify where your business is losing time, leads, or consistency — and recommend the strongest first AI workflow." />
        <link rel="canonical" href="https://theentityagency.com/book-efficiency-review" />
      </Helmet>

      <section className="border-b border-[#2a2620]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-16 sm:pt-20 pb-8">
          <nav className="font-mono-eav text-[11px] tracking-[0.2em] uppercase text-[#8a7e6d]">
            <Link to="/ai-operations" className="hover:text-[#b8963e]">AI Operations</Link>
            <span className="mx-2">/</span>
            <span className="text-[#b8963e]">Book Free Review</span>
          </nav>
          <p className="mt-6 overline">Free · 15–20 minute conversation</p>
          <h1 data-testid="review-form-title"
              className="mt-3 font-serif-display text-[36px] sm:text-[52px] leading-[1.05] text-[#f4efe6]"
              style={{ fontWeight: 600 }}>
            Book a free AI Efficiency Review.
          </h1>
          <p className="mt-6 text-lg text-[#c9bca4] leading-relaxed max-w-2xl">
            Tell us a little about your business. We'll review your submission within one business day and reach out to schedule a focused conversation.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
          {submitted ? (
            <div data-testid="review-submitted-state"
                 className="border border-[#b8963e] bg-[#0e0c0a] p-8 sm:p-12 text-center">
              <CheckCircle2 className="w-14 h-14 text-[#b8963e] mx-auto" />
              <h2 className="mt-6 font-serif-display text-[28px] sm:text-[36px] text-[#f4efe6]" style={{ fontWeight: 600 }}>
                Request received.
              </h2>
              <p className="mt-4 text-[#c9bca4] max-w-lg mx-auto leading-relaxed">
                Thanks — we've saved your Efficiency Review request. A team member will reach out to <strong className="text-[#f4efe6]">{form.work_email}</strong> within one business day to schedule your session.
              </p>
              <p className="mt-2 text-[#8a7e6d] text-sm">
                Check your inbox for a confirmation email. If you don't see it in ~5 minutes, check your spam folder.
              </p>
              <div className="mt-10 flex flex-wrap gap-3 justify-center">
                <Link to="/ai-operations" className="btn-ghost">Back to AI Operations</Link>
                <Link to="/signup" className="btn-gold">Run Free Visibility Snapshot <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="review-form" className="space-y-8">
              {/* Progress indicator */}
              <div className="flex items-center gap-3 font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">
                <span className={step >= 1 ? "text-[#b8963e]" : ""}>01 Contact</span>
                <span>—</span>
                <span className={step >= 2 ? "text-[#b8963e]" : ""}>02 About your business</span>
              </div>

              {step === 1 && (
                <div className="space-y-5" data-testid="review-form-step-1">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="First name*" name="first_name" value={form.first_name} onChange={update("first_name")} required />
                    <Field label="Last name*" name="last_name" value={form.last_name} onChange={update("last_name")} required />
                  </div>
                  <Field label="Business name*" name="business_name" value={form.business_name} onChange={update("business_name")} required />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field type="email" label="Work email*" name="work_email" value={form.work_email} onChange={update("work_email")} required />
                    <Field label="Phone (optional)" name="phone" value={form.phone} onChange={update("phone")} />
                  </div>
                  <Field label="Website" name="website" value={form.website} onChange={update("website")} placeholder="https://" />
                  <div className="pt-4 flex justify-end">
                    <button type="button" onClick={() => setStep(2)} disabled={!step1Valid}
                            data-testid="review-form-next-btn"
                            className={step1Valid ? "btn-gold" : "btn-gold opacity-40 cursor-not-allowed"}>
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5" data-testid="review-form-step-2">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Select label="Industry" value={form.industry} onChange={update("industry")} options={INDUSTRY_OPTIONS} />
                    <Select label="Number of employees" value={form.employees} onChange={update("employees")} options={EMPLOYEE_RANGES} />
                  </div>
                  <TextArea label="Primary business challenge"
                            value={form.primary_challenge}
                            onChange={update("primary_challenge")}
                            placeholder="What's the biggest thing slowing your business down right now?" />
                  <TextArea label="Most time-consuming repetitive task"
                            value={form.time_consuming_task}
                            onChange={update("time_consuming_task")}
                            placeholder="e.g. Rewriting proposals, chasing follow-ups, answering the same customer questions." />
                  <TextArea label="How new leads currently reach the business"
                            value={form.lead_channels}
                            onChange={update("lead_channels")}
                            placeholder="e.g. Website forms, email, phone calls, referrals, social DMs." />
                  <TextArea label="Software currently used"
                            value={form.software_used}
                            onChange={update("software_used")}
                            placeholder="e.g. Gmail, HubSpot, Google Workspace, QuickBooks, Calendly." />
                  <Field label="Preferred meeting time"
                         value={form.preferred_time}
                         onChange={update("preferred_time")}
                         placeholder="e.g. Weekday mornings ET, or a specific date." />
                  <label className="flex items-start gap-3 text-sm text-[#c9bca4] cursor-pointer">
                    <input type="checkbox" checked={form.consent} onChange={update("consent")}
                           data-testid="review-form-consent"
                           className="mt-1 accent-[#b8963e] w-4 h-4 shrink-0" />
                    <span>I consent to being contacted by The Entity Agency about my request. We do not sell or share your information.</span>
                  </label>
                  {error && <p className="text-sm text-[#c46a5a]" data-testid="review-form-error">{error}</p>}
                  <div className="pt-4 flex flex-wrap items-center justify-between gap-3">
                    <button type="button" onClick={() => setStep(1)} className="btn-ghost">
                      ← Back
                    </button>
                    <button type="submit" disabled={submitting || !form.consent}
                            data-testid="review-form-submit-btn"
                            className={submitting || !form.consent ? "btn-gold opacity-60 cursor-not-allowed" : "btn-gold"}>
                      {submitting ? "Submitting…" : "Submit request"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, ...rest }) {
  return (
    <label className="block">
      <span className="block font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d] mb-2">{label}</span>
      <input
        {...rest}
        className="w-full bg-[#0a0908] border border-[#2a2620] focus:border-[#b8963e] outline-none text-[#f4efe6] px-4 py-3 transition-colors"
      />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d] mb-2">{label}</span>
      <select value={value} onChange={onChange}
              className="w-full bg-[#0a0908] border border-[#2a2620] focus:border-[#b8963e] outline-none text-[#f4efe6] px-4 py-3 transition-colors">
        {options.map((o) => (
          <option key={o} value={o}>{o || "Select…"}</option>
        ))}
      </select>
    </label>
  );
}

function TextArea({ label, ...rest }) {
  return (
    <label className="block">
      <span className="block font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d] mb-2">{label}</span>
      <textarea
        rows={3}
        {...rest}
        className="w-full bg-[#0a0908] border border-[#2a2620] focus:border-[#b8963e] outline-none text-[#f4efe6] px-4 py-3 transition-colors resize-y"
      />
    </label>
  );
}
